/**
 * Sends anonymous game events to our own Worker (POST /e → Cloudflare Analytics Engine).
 * Off in dev/e2e/playtest builds, and when the browser asks not to be tracked (Global Privacy
 * Control or Do Not Track). Fire-and-forget: tracking can never break or slow the game.
 */
import { drafts } from "../puzzles/load";
import { activity, type GameEvent, type Seen } from "./events";
import { store } from "./storage";
import { vault7 } from "./vault";

type Via = NonNullable<GameEvent["v"]>;
let via: Via = "direct";

/** How this visit arrived; reported with the day's "active" event. */
export const setVia = (v: Via): void => {
  via = v;
};

function enabled(): boolean {
  if (drafts) return false;
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return !(nav.globalPrivacyControl === true || nav.doNotTrack === "1");
}

function device(): NonNullable<GameEvent["w"]> {
  const w = Math.min(window.innerWidth || 0, screen?.width || Infinity);
  return w < 600 ? "phone" : w < 1024 ? "tablet" : "desktop";
}

export function track(ev: GameEvent): void {
  if (!enabled()) return;
  try {
    const body = JSON.stringify({ ...ev, w: device() });
    const blob = new Blob([body], { type: "text/plain" });
    if (!navigator.sendBeacon?.("/e", blob)) void fetch("/e", { method: "POST", body, keepalive: true }).catch(() => undefined);
  } catch {
    /* never let analytics break the game */
  }
}

/** Once per device per local day: counts players, new players, and who came back. */
export function trackActive(today: string): void {
  if (!enabled()) return;
  const a = activity(store.get<Seen>("seen"), today, vault7(store.get<string>("vaultLast"), today));
  if (!a) return;
  store.set("seen", a.next);
  track({ ...a.event, v: via });
}
